import { doc, updateDoc } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import type { QuickSettings as QuickSettingsType } from '../../components/TenderDetails/types';
import { firestore } from '../../services/firebase';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';
import { logTenderActivity } from '../../utils/activityLogger';

const EMPTY_OBJECT: Record<string, never> = {};
const EMPTY_ARRAY: never[] = [];

export const useStructureQuickSettings = (
  tender: Tender | null,
  account: (User & { clientId?: string; aiSettings?: unknown }) | null,
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void
) => {
  const [quickSettings, setQuickSettings] = useState<QuickSettingsType>({
    enabled: true,
  });
  const isInitialMount = useRef(true);

  const handleQuickSettingsChange = (newSettings: Partial<QuickSettingsType>) => {
    setQuickSettings((prev) => ({ ...prev, ...newSettings }));
  };

  useEffect(() => {
    const hasForms = (tender?.detectedForms || EMPTY_ARRAY).length > 0;
    const defaultFormatMode = hasForms ? ('hybrid' as const) : ('narrative' as const);

    if (tender?.quickSettings) {
      setQuickSettings((prev) => {
        const next = {
          ...tender.quickSettings,
          enabled: tender.quickSettings?.enabled ?? false,
          draftFormatMode: tender.quickSettings?.draftFormatMode || defaultFormatMode,
        };
        if (JSON.stringify(prev) === JSON.stringify(next)) {
          return prev;
        }
        return next;
      });
    } else if (account?.aiSettings) {
      setQuickSettings((prev) => {
        const next = {
          ...(account.aiSettings as Record<string, unknown>),
          enabled: true,
          draftFormatMode: defaultFormatMode,
        };
        if (JSON.stringify(prev) === JSON.stringify(next)) {
          return prev;
        }
        return next;
      });
    } else {
      setQuickSettings((prev) => {
        const next = {
          enabled: true,
          draftFormatMode: defaultFormatMode,
        };
        if (JSON.stringify(prev) === JSON.stringify(next)) {
          return prev;
        }
        return next;
      });
    }
  }, [tender?.quickSettings, account?.aiSettings, tender?.detectedForms]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!account?.clientId || !account?.uid || !tender?.id) {
      return;
    }

    // Check for real differences to avoid redundant saves and snackbars
    const serverSettings: Partial<QuickSettingsType> = tender?.quickSettings || EMPTY_OBJECT;
    const hasForms = (tender?.detectedForms || EMPTY_ARRAY).length > 0;
    const defaultFormatMode = hasForms ? ('hybrid' as const) : ('narrative' as const);

    const normalizedServer = {
      ...serverSettings,
      enabled: serverSettings.enabled ?? false,
      draftFormatMode: serverSettings.draftFormatMode || defaultFormatMode,
    };

    const normalizedLocal = {
      ...quickSettings,
      enabled: quickSettings.enabled ?? true,
      draftFormatMode: quickSettings.draftFormatMode || defaultFormatMode,
    };

    // Deep comparison using stringify (safe enough for this small object)
    if (JSON.stringify(normalizedLocal) === JSON.stringify(normalizedServer)) {
      return;
    }

    const handler = setTimeout(async () => {
      try {
        if (!tender?.id) {
          return;
        }
        const tenderDocRef = doc(firestore, 'tenders', tender.id);
        // Firestore throws an error if any property is undefined, even if nested.
        // A simple stringify/parse deeply removes all keys that have undefined values.
        const cleanSettings = JSON.parse(JSON.stringify(normalizedLocal));

        await updateDoc(tenderDocRef, { quickSettings: cleanSettings });
        showSnackbar('Quick settings saved!', 'success');
        logTenderActivity.updated({
          id: tender.id,
          title: tender.title || '',
          updateType: 'quick_settings_update',
        });
      } catch (_error) {
        showSnackbar('Failed to save quick settings', 'error');
      }
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [
    quickSettings,
    account?.clientId,
    account?.uid,
    tender?.id,
    showSnackbar,
    tender?.title,
    tender?.quickSettings,
    tender?.detectedForms,
  ]);

  return {
    quickSettings,
    handleQuickSettingsChange,
  };
};
